import React from "react";
import { PageHeader } from "../../field-service/components/PageHeader/PageHeader";

// ─── Types ────────────────────────────────────────────────────────────────────

type WOType = "work-order" | "preventive" | "part-order" | "energy" | "documentation";
type WOStatus = "OPEN" | "IN_PROGRESS" | "COMPLETED" | "ON_HOLD" | "OVERDUE";

interface SchedulerWO {
  id: string;
  number: string;
  title: string;
  type: WOType;
  company: string;
  assetName: string;
  status: WOStatus;
  priority: "low" | "medium" | "high";
  scheduledDate?: string; // ISO date string
  assignedMemberId?: string;
  assignees?: { initials: string }[];
  notificationCount?: number;
  team?: string;
}

interface Member {
  id: string;
  name: string;
  initials: string;
  role: string;
  avatarColor: string;
}

type CalendarView = "week" | "month";

// ─── Constants ────────────────────────────────────────────────────────────────

const WO_TYPE_CONFIG: Record<WOType, { color: string; bg: string; label: string }> = {
  "work-order":    { color: "#6974fb", bg: "#e6e8fe", label: "Work Order" },
  "preventive":   { color: "#00b09b", bg: "#e0faf6", label: "Preventive" },
  "part-order":   { color: "#ff7a00", bg: "#fff0e6", label: "Part Order" },
  "energy":       { color: "#10b981", bg: "#e0f8e8", label: "Energy" },
  "documentation":{ color: "#9333ea", bg: "#f3e8ff", label: "Documentation" },
};

const STATUS_CONFIG: Record<WOStatus, { bg: string; color: string; label: string }> = {
  OPEN:        { bg: "#ccefeb", color: "#00b09b", label: "OPEN" },
  IN_PROGRESS: { bg: "#fff0e6", color: "#ff7a00", label: "IN PROGRESS" },
  COMPLETED:   { bg: "#e6f9f1", color: "#10b981", label: "COMPLETED" },
  ON_HOLD:     { bg: "#f3f4f6", color: "#748094", label: "ON HOLD" },
  OVERDUE:     { bg: "#fde5f0", color: "#ee0064", label: "OVERDUE" },
};

const MOCK_MEMBERS: Member[] = [
  { id: "m1", name: "Marcus Zhang",    initials: "MZ", role: "Field Technician",  avatarColor: "#6974fb" },
  { id: "m2", name: "Sara Patel",      initials: "SP", role: "Service Engineer",  avatarColor: "#00b09b" },
  { id: "m3", name: "Ben Parker",      initials: "BP", role: "Maintenance Lead",  avatarColor: "#ff7a00" },
  { id: "m4", name: "Lena Fischer",    initials: "LF", role: "Technician",        avatarColor: "#9333ea" },
  { id: "m5", name: "Omar Hassan",     initials: "OH", role: "Field Technician",  avatarColor: "#10b981" },
];

const MOCK_WORK_ORDERS: SchedulerWO[] = [
  { id: "1",  number: "WBRW10321", title: "Asset Repair & Maintenance",   type: "work-order",    company: "Acme International",  assetName: "CNC Machine A",   status: "OPEN",   priority: "high",   assignees: [{ initials: "MZ" }, { initials: "SP" }, { initials: "BP" }], notificationCount: 2, team: "Team A" },
  { id: "2",  number: "WBRW10322", title: "Facility Maintenance Task",    type: "preventive",    company: "Beta Solutions",      assetName: "HVAC Unit 3",     status: "OPEN",   priority: "medium", assignees: [{ initials: "LF" }], notificationCount: 1, team: "Team B" },
  { id: "3",  number: "WBRW10323", title: "Machinery Repair Order",       type: "part-order",    company: "Gamma Enterprises",   assetName: "Lathe Machine B", status: "OPEN",   priority: "low",    assignees: [{ initials: "OH" }, { initials: "MZ" }], notificationCount: 0, team: "Team A" },
  { id: "4",  number: "WBRW10324", title: "Equipment Overhaul Request",   type: "work-order",    company: "Delta Corp",          assetName: "Press Machine 2", status: "OPEN",   priority: "high",   assignees: [{ initials: "SP" }, { initials: "BP" }, { initials: "LF" }], notificationCount: 3, team: "Team C" },
  { id: "5",  number: "WBRW10325", title: "Infrastructure Upgrade",       type: "energy",        company: "Epsilon Industries",  assetName: "Generator Unit",  status: "OPEN",   priority: "medium", assignees: [{ initials: "MZ" }], notificationCount: 0, team: "Team B" },
  { id: "6",  number: "WBRW10326", title: "Vehicle Service Order",        type: "documentation", company: "Zeta Manufacturing",  assetName: "Forklift #5",     status: "OPEN",   priority: "low",    assignees: [{ initials: "OH" }], notificationCount: 1, team: "Team A" },
  { id: "7",  number: "WBRW10327", title: "System Calibration Request",   type: "work-order",    company: "Acme International",  assetName: "Sensor Array X",  status: "OPEN",   priority: "medium", assignees: [{ initials: "BP" }, { initials: "SP" }], notificationCount: 2, team: "Team C" },
  { id: "8",  number: "WBRW10328", title: "Conveyor Belt Inspection",     type: "preventive",    company: "Beta Solutions",      assetName: "Conveyor A",      status: "OPEN",   priority: "high",   assignees: [{ initials: "LF" }, { initials: "OH" }, { initials: "MZ" }], notificationCount: 4, team: "Team B" },
  { id: "9",  number: "WBRW10329", title: "Pump Station Servicing",       type: "part-order",    company: "Delta Corp",          assetName: "Pump Unit 7",     status: "OPEN",   priority: "low",    assignees: [{ initials: "SP" }], notificationCount: 0, team: "Team A" },
  { id: "10", number: "WBRW10330", title: "Electrical Panel Maintenance", type: "energy",        company: "Gamma Enterprises",   assetName: "Panel E-12",      status: "OPEN",   priority: "medium", assignees: [{ initials: "MZ" }, { initials: "BP" }], notificationCount: 1, team: "Team C" },
];

// Pre-assign some WOs to members and dates
const getInitialAssignments = (): SchedulerWO[] => {
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().split("T")[0];
  const d = (offset: number) => { const x = new Date(today); x.setDate(x.getDate() + offset); return fmt(x); };

  return [
    ...MOCK_WORK_ORDERS,
    { id: "a1", number: "WBRW10331", title: "Scheduled Pressure Test",      type: "preventive",    company: "Acme International", assetName: "Pressure Vessel", status: "IN_PROGRESS", priority: "high",   scheduledDate: d(0),  assignedMemberId: "m1" },
    { id: "a2", number: "WBRW10332", title: "Weekly Safety Inspection",     type: "work-order",    company: "Beta Solutions",     assetName: "Safety Zone A",  status: "OPEN",        priority: "medium", scheduledDate: d(1),  assignedMemberId: "m2" },
    { id: "a3", number: "WBRW10333", title: "Lubrication Service",          type: "preventive",    company: "Delta Corp",         assetName: "Gear Unit C",    status: "OPEN",        priority: "low",    scheduledDate: d(1),  assignedMemberId: "m1" },
    { id: "a4", number: "WBRW10334", title: "Filter Replacement",           type: "part-order",    company: "Gamma Enterprises",  assetName: "HVAC Unit 5",    status: "OPEN",        priority: "medium", scheduledDate: d(2),  assignedMemberId: "m3" },
    { id: "a5", number: "WBRW10335", title: "Turbine Alignment Check",      type: "energy",        company: "Epsilon Industries", assetName: "Turbine T-1",    status: "OPEN",        priority: "high",   scheduledDate: d(2),  assignedMemberId: "m4" },
    { id: "a6", number: "WBRW10336", title: "Cooling Tower Inspection",     type: "documentation", company: "Acme International", assetName: "Cooling Tower 2",status: "OPEN",        priority: "low",    scheduledDate: d(3),  assignedMemberId: "m2" },
    { id: "a7", number: "WBRW10337", title: "Hydraulic System Check",       type: "work-order",    company: "Beta Solutions",     assetName: "Press Unit 4",   status: "OVERDUE",     priority: "high",   scheduledDate: d(-1), assignedMemberId: "m5" },
    { id: "a8", number: "WBRW10338", title: "Motor Bearing Replacement",    type: "part-order",    company: "Delta Corp",         assetName: "Motor M-09",     status: "OPEN",        priority: "medium", scheduledDate: d(4),  assignedMemberId: "m3" },
    { id: "a9", number: "WBRW10339", title: "Compressor Calibration",       type: "energy",        company: "Zeta Manufacturing", assetName: "Compressor C3",  status: "IN_PROGRESS", priority: "high",   scheduledDate: d(0),  assignedMemberId: "m5" },
  ];
};

// ─── Priority badge ───────────────────────────────────────────────────────────

const PRIORITY_CONFIG = {
  high:   { color: "#ee0064", bg: "#fde5f0", label: "High" },
  medium: { color: "#ff7a00", bg: "#fff0e6", label: "Medium" },
  low:    { color: "#748094", bg: "#f3f4f6", label: "Low" },
};

// ─── Helper: week days ────────────────────────────────────────────────────────

function getWeekDays(anchor: Date): Date[] {
  const start = new Date(anchor);
  const dow = start.getDay(); // 0=Sun
  start.setDate(start.getDate() - (dow === 0 ? 6 : dow - 1)); // Monday
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

function getMonthDays(anchor: Date): Date[] {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDow = first.getDay(); // 0=Sun
  const offset = startDow === 0 ? 6 : startDow - 1; // days before month start (Mon-based)
  const totalCells = Math.ceil((offset + last.getDate()) / 7) * 7;
  return Array.from({ length: totalCells }, (_, i) => {
    const d = new Date(first);
    d.setDate(d.getDate() - offset + i);
    return d;
  });
}

function isoDate(d: Date) {
  return d.toISOString().split("T")[0];
}

function sameDate(a: Date, b: Date) {
  return isoDate(a) === isoDate(b);
}

function formatMonthYear(d: Date) {
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

function formatWeekRange(days: Date[]) {
  const first = days[0];
  const last = days[days.length - 1];
  if (first.getMonth() === last.getMonth()) {
    return `${first.getDate()} – ${last.getDate()} ${first.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`;
  }
  return `${first.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${last.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
}

// ─── WO type icons ────────────────────────────────────────────────────────────

function WOTypeIcon({ type, color }: { type: WOType; color: string }) {
  const s = { stroke: color, strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, fill: "none" };
  switch (type) {
    case "work-order": return (
      <svg width="18" height="18" viewBox="0 0 20 20" {...s}>
        <path d="M6 2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" />
        <path d="M7 7h6M7 10h6M7 13h4" />
      </svg>
    );
    case "preventive": return (
      <svg width="18" height="18" viewBox="0 0 20 20" {...s}>
        <circle cx="10" cy="10" r="7" />
        <path d="M10 6v4l3 3" />
      </svg>
    );
    case "part-order": return (
      <svg width="18" height="18" viewBox="0 0 20 20" {...s}>
        <path d="M12 3l5 5-8 8-5 1 1-5 8-8z" />
        <path d="M10.5 5.5l4 4" />
      </svg>
    );
    case "energy": return (
      <svg width="18" height="18" viewBox="0 0 20 20" {...s}>
        <path d="M11 2L5 11h7l-1 7 8-10h-7l2-6z" />
      </svg>
    );
    case "documentation": return (
      <svg width="18" height="18" viewBox="0 0 20 20" {...s}>
        <rect x="3" y="2" width="14" height="16" rx="2" />
        <path d="M7 7h6M7 10h6M7 13h4" />
      </svg>
    );
  }
}

const GlobeIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="1.2" strokeLinecap="round">
    <circle cx="6" cy="6" r="5" />
    <path d="M6 1c-1.5 1.4-2.5 3-2.5 5s1 3.6 2.5 5M6 1c1.5 1.4 2.5 3 2.5 5S7.5 10.6 6 11" />
    <path d="M1 6h10" />
  </svg>
);

// ─── WO Card (left panel) ─────────────────────────────────────────────────────

function WOCard({ wo, onDragStart }: { wo: SchedulerWO; onDragStart: (id: string) => void }) {
  const typeConf = WO_TYPE_CONFIG[wo.type];
  const statusConf = STATUS_CONFIG[wo.status];
  const assignees = wo.assignees ?? [];
  const shown = assignees.slice(0, 1);
  const extra = assignees.length - 1;

  return (
    <div
      draggable
      onDragStart={() => onDragStart(wo.id)}
      style={{
        background: "#fff",
        border: "1px solid #e8ecf2",
        borderRadius: 8,
        padding: "14px 14px 12px",
        cursor: "grab",
        userSelect: "none",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      }}
    >
      {/* Row 1: icon + number + status */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        {/* Type icon with badges */}
        <div style={{ position: "relative", width: 36, height: 36, flexShrink: 0 }}>
          {/* Globe badge — top-right */}
          <div style={{
            position: "absolute", top: -5, right: -5, zIndex: 1,
            width: 16, height: 16, borderRadius: "50%",
            background: "#3d3d5c",
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "1.5px solid #fff",
          }}>
            <GlobeIcon />
          </div>
          {/* Icon container */}
          <div style={{
            width: 36, height: 36, borderRadius: 9,
            background: typeConf.bg,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <WOTypeIcon type={wo.type} color={typeConf.color} />
          </div>
          {/* Notification badge — bottom-left */}
          {!!wo.notificationCount && wo.notificationCount > 0 && (
            <div style={{
              position: "absolute", bottom: -5, left: -5, zIndex: 1,
              minWidth: 16, height: 16, borderRadius: 8,
              background: "#ee0064",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 9, fontWeight: 800, color: "#fff",
              border: "1.5px solid #fff",
              padding: "0 3px",
              fontFamily: "var(--font-family-base)",
            }}>
              {wo.notificationCount}
            </div>
          )}
        </div>

        {/* WO number */}
        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#748094", letterSpacing: -0.1 }}>
          #{wo.number}
        </span>

        {/* Status badge */}
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          padding: "4px 6px", borderRadius: 4,
          fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
          background: statusConf.bg, color: statusConf.color,
          fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
        }}>
          {statusConf.label}
        </span>
      </div>

      {/* Title */}
      <div style={{ fontSize: 14, fontWeight: 800, color: "#111827", lineHeight: 1.35, marginBottom: 4 }}>
        {wo.title}
      </div>

      {/* Company */}
      <div style={{ fontSize: 12, fontWeight: 500, color: "#9ca3af", marginBottom: 14 }}>
        {wo.company}
      </div>

      {/* Bottom row: assignee chips + team */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 5 }}>
          {shown.map((a, i) => (
            <span key={i} style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              padding: "4px 6px", borderRadius: 4,
              fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
              background: "var(--color-bg-accent)", color: "var(--color-content-secondary)",
              fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
            }}>
              {a.initials}
            </span>
          ))}
          {extra > 0 && (
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              padding: "4px 6px", borderRadius: 4,
              fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
              background: "var(--color-bg-accent)", color: "var(--color-content-secondary)",
              fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
            }}>
              +{extra}
            </span>
          )}
        </div>
        {wo.team && (
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            padding: "4px 6px", borderRadius: 4,
            fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
            background: "var(--color-bg-brand-lightest)", color: "var(--color-content-brand)",
            fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
          }}>
            {wo.team.toUpperCase()}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Calendar WO chip ─────────────────────────────────────────────────────────

function WOChip({ wo, onClick }: { wo: SchedulerWO; onClick?: (e: React.MouseEvent) => void }) {
  const typeConf = WO_TYPE_CONFIG[wo.type];
  return (
    <div
      title={`${wo.number} – ${wo.title}`}
      onClick={onClick}
      style={{
        background: typeConf.bg,
        borderLeft: `3px solid ${typeConf.color}`,
        borderRadius: 6,
        padding: "5px 8px",
        fontSize: 12,
        fontWeight: 600,
        color: "#0d2847",
        fontFamily: "var(--font-family-base)",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        cursor: "pointer",
        marginBottom: 3,
        lineHeight: "16px",
        letterSpacing: "-0.1px",
      }}
    >
      {wo.title}
    </div>
  );
}

// ─── PM Event chip ────────────────────────────────────────────────────────────

function PMEventChip({ event, onClick }: { event: PMSchedulerEvent; onClick?: (e: React.MouseEvent) => void }) {
  const conf = PM_STATUS_CONFIG[event.status];
  return (
    <div
      title={`${event.title} · ${event.status}`}
      onClick={onClick}
      style={{
        background: conf.bg,
        borderLeft: `3px solid ${conf.color}`,
        borderRadius: 6,
        padding: "5px 8px",
        fontSize: 12,
        fontWeight: 600,
        color: "#0d2847",
        fontFamily: "var(--font-family-base)",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        cursor: "pointer",
        marginBottom: 3,
        lineHeight: "16px",
        letterSpacing: "-0.1px",
      }}
    >
      {event.title}
    </div>
  );
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend() {
  return (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px 16px" }}>
      {(Object.entries(WO_TYPE_CONFIG) as [WOType, typeof WO_TYPE_CONFIG[WOType]][]).map(([, conf]) => (
        <div key={conf.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: conf.color, display: "inline-block" }} />
          <span style={{ fontSize: 11, color: "#748094", fontWeight: 500 }}>{conf.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Scheduler Popup ──────────────────────────────────────────────────────────

function PopupRow({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ flexShrink: 0, color: "#748094", display: "flex", alignItems: "center" }}>{icon}</span>
      <span style={{ fontSize: 13, color: "#1a2233", fontWeight: 500, lineHeight: "18px" }}>{children}</span>
    </div>
  );
}

function SchedulerPopup({ data, onClose }: { data: PopupData; onClose: () => void }) {
  const POPUP_W = 304;
  let left = data.x + 14;
  let top = data.y - 16;
  if (typeof window !== "undefined") {
    if (left + POPUP_W > window.innerWidth - 16) left = data.x - POPUP_W - 14;
    if (top + 360 > window.innerHeight - 16) top = window.innerHeight - 360 - 16;
    if (top < 16) top = 16;
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 998 }} />
      <div style={{
        position: "fixed", left, top, zIndex: 999,
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 8px 32px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.07)",
        width: POPUP_W,
        padding: "20px",
        fontFamily: "var(--font-family-base)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 18 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: "#e6e8fe", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3 5.5A1.5 1.5 0 014.5 4h3.086a1.5 1.5 0 011.06.44l.915.914A1.5 1.5 0 0010.62 6H15.5A1.5 1.5 0 0117 7.5v7A1.5 1.5 0 0115.5 16h-11A1.5 1.5 0 013 14.5v-9z" stroke="#6974fb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#0d2847", lineHeight: "20px", paddingTop: 3 }}>
            {data.title}
          </span>
        </div>

        {/* Detail rows */}
        <div style={{ display: "flex", flexDirection: "column", gap: 11, marginBottom: 20 }}>
          <PopupRow icon={
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 2L10.5 6.5H13L9.5 9.5L11 14L8 11.5L5 14L6.5 9.5L3 6.5H5.5L8 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
            </svg>
          }>
            {data.assetLabel}
          </PopupRow>
          <PopupRow icon={
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="4" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M2 7h12" stroke="currentColor" strokeWidth="1.4"/>
              <rect x="5" y="1.5" width="1.4" height="3" rx="0.7" fill="currentColor"/>
              <rect x="9.6" y="1.5" width="1.4" height="3" rx="0.7" fill="currentColor"/>
            </svg>
          }>
            {data.company}
          </PopupRow>
          <PopupRow icon={
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="4" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M2 7h12" stroke="currentColor" strokeWidth="1.4"/>
              <rect x="5" y="1.5" width="1.4" height="3" rx="0.7" fill="currentColor"/>
              <rect x="9.6" y="1.5" width="1.4" height="3" rx="0.7" fill="currentColor"/>
            </svg>
          }>
            {formatPopupDate(data.date)} <span style={{ color: "#748094" }}>• All Day</span>
          </PopupRow>
          <PopupRow icon={
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 6.5V9l1.5 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M5.5 2.5h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          }>
            Due on {addPopupDays(data.date, 7)}
          </PopupRow>
          {data.assigneeName && (
            <PopupRow icon={
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M2.5 13.5c0-2.485 2.462-4.5 5.5-4.5s5.5 2.015 5.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            }>
              {data.assigneeName}
            </PopupRow>
          )}
          {data.team && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ flexShrink: 0, color: "#748094", display: "flex", alignItems: "center" }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="5.5" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.4"/>
                  <circle cx="10.5" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M1 13c0-2 2-3.5 4.5-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  <path d="M7 13c0-2 1.567-3.5 3.5-3.5S14 11 14 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </span>
              <span style={{
                background: "#e6e8fe", color: "#6974fb",
                fontWeight: 700, fontSize: 11,
                padding: "3px 10px", borderRadius: 6,
                letterSpacing: "0.4px",
                fontFamily: "var(--font-family-base)",
              }}>
                {data.team.toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* CTA buttons */}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {data.type === "wo" && (
            <button style={{
              padding: "9px 16px", borderRadius: 8,
              border: "1.5px solid #e5e9f0",
              background: "#fff", fontSize: 13, fontWeight: 600, color: "#1a2233",
              cursor: "pointer", fontFamily: "var(--font-family-base)",
            }}>
              View Work Order
            </button>
          )}
          <button style={{
            padding: "9px 16px", borderRadius: 8,
            border: "none",
            background: "#2563eb", fontSize: 13, fontWeight: 600, color: "#fff",
            cursor: "pointer", fontFamily: "var(--font-family-base)",
          }}>
            {data.type === "wo" ? "Reschedule" : "Create Work Order"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const SORT_OPTIONS = ["Work Order Title", "Priority", "Due Date", "Status", "Assignee"] as const;
type SortOption = typeof SORT_OPTIONS[number];

export function SchedulerPage() {
  const [allWOs, setAllWOs] = React.useState<SchedulerWO[]>(getInitialAssignments);
  const [search, setSearch] = React.useState("");
  const [view, setView] = React.useState<CalendarView>("week");
  const [anchor, setAnchor] = React.useState(new Date());
  const [dragId, setDragId] = React.useState<string | null>(null);
  const [dropTarget, setDropTarget] = React.useState<{ memberId: string; date: string } | null>(null);
  const [colView, setColView] = React.useState<"members" | "assets">("members");
  const [sortBy, setSortBy] = React.useState<SortOption>("Work Order Title");
  const [sortOpen, setSortOpen] = React.useState(false);
  const [viewDropOpen, setViewDropOpen] = React.useState(false);
  const [popup, setPopup] = React.useState<PopupData | null>(null);
  const sortRef = React.useRef<HTMLDivElement>(null);
  const viewDropRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!sortOpen && !viewDropOpen) return;
    function handleClick(e: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
      if (viewDropRef.current && !viewDropRef.current.contains(e.target as Node)) setViewDropOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [sortOpen, viewDropOpen]);

  const unscheduled = allWOs.filter(wo => !wo.scheduledDate && !wo.assignedMemberId);
  const scheduled   = allWOs.filter(wo => wo.scheduledDate && wo.assignedMemberId);

  const filteredUnscheduled = unscheduled.filter(wo =>
    !search || wo.title.toLowerCase().includes(search.toLowerCase()) ||
    wo.number.toLowerCase().includes(search.toLowerCase()) ||
    wo.company.toLowerCase().includes(search.toLowerCase())
  );

  // Calendar state
  const weekDays = getWeekDays(anchor);
  function navPrev() {
    const d = new Date(anchor);
    if (view === "week") d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setAnchor(d);
  }

  function navNext() {
    const d = new Date(anchor);
    if (view === "week") d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setAnchor(d);
  }

  function navToday() {
    setAnchor(new Date());
  }

  function handleDragStart(id: string) {
    setDragId(id);
  }

  function handleDrop(memberId: string, date: string) {
    if (!dragId) return;
    setAllWOs(prev => prev.map(wo =>
      wo.id === dragId ? { ...wo, scheduledDate: date, assignedMemberId: memberId } : wo
    ));
    setDragId(null);
    setDropTarget(null);
  }

  function handleDragOver(memberId: string, date: string, e: React.DragEvent) {
    e.preventDefault();
    setDropTarget({ memberId, date });
  }

  function handleDragLeave() {
    setDropTarget(null);
  }

  function getWOsForCell(memberId: string, date: Date): SchedulerWO[] {
    const iso = isoDate(date);
    return scheduled.filter(wo => wo.assignedMemberId === memberId && wo.scheduledDate === iso);
  }

  function getPMEventsForCell(assetId: string, date: Date): PMSchedulerEvent[] {
    const iso = isoDate(date);
    return (MOCK_ASSET_PM_EVENTS[assetId] ?? []).filter(e => e.scheduledDate === iso);
  }

  const today = new Date();
  const calTitle = view === "week"
    ? formatWeekRange(weekDays)
    : formatMonthYear(anchor);

  const headerActions = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {/* Sort dropdown */}
      <div ref={sortRef} style={{ position: "relative" }}>
        <button
          onClick={() => setSortOpen(o => !o)}
          style={{
            height: 40,
            padding: "0 14px",
            background: "#fff",
            border: "1px solid var(--color-border-primary)",
            borderRadius: "var(--radius-s)",
            fontFamily: "var(--font-family-base)",
            fontSize: 12,
            fontWeight: 700,
            color: "var(--color-content-primary)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            whiteSpace: "nowrap",
          }}
        >
          {sortBy}
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 5l4 4 4-4" />
          </svg>
        </button>
        {sortOpen && (
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 50,
            background: "#fff", border: "1px solid var(--color-border-primary)",
            borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            minWidth: 180, overflow: "hidden",
          }}>
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt}
                onClick={() => { setSortBy(opt); setSortOpen(false); }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "9px 14px",
                  fontSize: 12, fontWeight: sortBy === opt ? 700 : 500,
                  color: sortBy === opt ? "#1a2233" : "#748094",
                  background: sortBy === opt ? "#f0f2f7" : "transparent",
                  border: "none", cursor: "pointer",
                  fontFamily: "var(--font-family-base)",
                }}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Publish Schedule */}
      <button
        style={{
          height: 40,
          padding: "0 16px",
          background: "#1a07ff",
          border: "none",
          borderRadius: "var(--radius-s)",
          fontFamily: "var(--font-family-base)",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Publish Schedule
      </button>
    </div>
  );

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      overflow: "hidden",
      background: "#f5f6fa",
      fontFamily: "var(--font-family-base)",
    }}>
      <PageHeader
        breadcrumbs={[{ label: "Scheduler" }]}
        actions={headerActions}
      />

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
      {/* ── Left Panel: Unscheduled WOs ── */}
      <div style={{
        width: 280,
        minWidth: 260,
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        borderRight: "1px solid #e5e9f0",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 16px 12px", borderBottom: "1px solid #f0f2f7" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1a2233", marginBottom: 10 }}>
            Unscheduled Work Orders
            <span style={{
              marginLeft: 8, fontSize: 11, fontWeight: 700,
              background: "#f0f2f7", color: "#748094",
              padding: "2px 8px", borderRadius: 20,
            }}>
              {filteredUnscheduled.length}
            </span>
          </div>
          {/* Search */}
          <div style={{ position: "relative" }}>
            <svg style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
              width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="#a2aab8" strokeWidth="2" strokeLinecap="round">
              <circle cx="9" cy="9" r="6" /><path d="M15 15l3 3" />
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search work orders…"
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "7px 10px 7px 32px",
                fontSize: 12, border: "1px solid #e5e9f0",
                borderRadius: 8, outline: "none",
                background: "#f8f9fc", color: "#1a2233",
                fontFamily: "inherit",
              }}
            />
          </div>
        </div>

        {/* WO List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
          {filteredUnscheduled.length === 0 ? (
            <div style={{ textAlign: "center", color: "#a2aab8", fontSize: 12, marginTop: 32 }}>
              No unscheduled work orders
            </div>
          ) : (
            filteredUnscheduled.map(wo => (
              <WOCard key={wo.id} wo={wo} onDragStart={handleDragStart} />
            ))
          )}
        </div>

      </div>

      {/* ── Right Panel: Calendar ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Calendar Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 20px",
          background: "#fff",
          borderBottom: "1px solid #e5e9f0",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Nav arrows */}
            <button onClick={navPrev} style={navBtnStyle}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 4L6 8l4 4" /></svg>
            </button>
            <button onClick={navToday} style={{ ...navBtnStyle, padding: "5px 12px", fontSize: 12, fontWeight: 600 }}>Today</button>
            <button onClick={navNext} style={navBtnStyle}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 4l4 4-4 4" /></svg>
            </button>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#1a2233", marginLeft: 4 }}>{calTitle}</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* View dropdown */}
            <div ref={viewDropRef} style={{ position: "relative" }}>
              <button
                onClick={() => setViewDropOpen(o => !o)}
                style={{
                  height: 36,
                  padding: "0 12px",
                  background: "#fff",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: "var(--radius-s)",
                  fontFamily: "var(--font-family-base)",
                  fontSize: 12, fontWeight: 700,
                  color: "var(--color-content-primary)",
                  cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {view === "week" ? "Week" : "Month"}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 5l4 4 4-4" />
                </svg>
              </button>
              {viewDropOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 50,
                  background: "#fff", border: "1px solid var(--color-border-primary)",
                  borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  minWidth: 120, overflow: "hidden",
                }}>
                  {(["week", "month"] as CalendarView[]).map(v => (
                    <button key={v} onClick={() => { setView(v); setViewDropOpen(false); }} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      width: "100%", padding: "9px 14px", textAlign: "left",
                      fontSize: 12, fontWeight: view === v ? 700 : 500,
                      color: view === v ? "#1a2233" : "#748094",
                      background: view === v ? "#f0f2f7" : "transparent",
                      border: "none", cursor: "pointer", fontFamily: "var(--font-family-base)",
                      textTransform: "capitalize",
                    }}>
                      {v}
                      {view === v && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#6974fb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 6l3 3 5-5" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Calendar Body */}
        {view === "week" ? (
          <WeekView
            days={weekDays}
            members={MOCK_MEMBERS}
            today={today}
            getWOsForCell={getWOsForCell}
            getPMEventsForCell={getPMEventsForCell}
            dropTarget={dropTarget}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            scheduled={scheduled}
            colView={colView}
            onColViewChange={setColView}
            onChipClick={setPopup}
          />
        ) : (
          <MonthView
            anchor={anchor}
            today={today}
            members={MOCK_MEMBERS}
            scheduled={scheduled}
            getWOsForCell={getWOsForCell}
            getPMEventsForCell={getPMEventsForCell}
            dropTarget={dropTarget}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            colView={colView}
            onColViewChange={setColView}
            onChipClick={setPopup}
          />
        )}
      </div>
      </div>
      {popup && <SchedulerPopup data={popup} onClose={() => setPopup(null)} />}
    </div>
  );
}

// ─── Scheduler assets + PM events ────────────────────────────────────────────

interface SchedulerAsset {
  id: string;
  name: string;
  serial: string;
  company: string;
}

const MOCK_SCHEDULER_ASSETS: SchedulerAsset[] = [
  { id: "sa1", name: "Hydraulic Press MK-3000",      serial: "HP3K-001XP",   company: "Acme International"  },
  { id: "sa2", name: "CNC Machining Center X7",       serial: "CNCX7-002XP",  company: "Beta Solutions"      },
  { id: "sa3", name: "Industrial Robot Arm R-200",    serial: "IRA-R200XP",   company: "Delta Corp"          },
  { id: "sa4", name: "Conveyor Belt System CB-5",     serial: "CBS-005XP",    company: "Gamma Enterprises"   },
  { id: "sa5", name: "Laser Cutting Machine LC-100",  serial: "LCM-100XP",    company: "Epsilon Industries"  },
  { id: "sa6", name: "Air Compressor Unit AC-50",     serial: "ACU-050XP",    company: "Zeta Manufacturing"  },
  { id: "sa7", name: "HVAC Unit 3",                   serial: "HVAC-003XP",   company: "Beta Solutions"      },
  { id: "sa8", name: "Generator Unit G-200",          serial: "GU-200XP",     company: "Acme International"  },
];

type PMStatus = "Pending" | "Open" | "In Progress" | "Closed" | "Completed";

interface PopupData {
  type: "wo" | "pm";
  title: string;
  assetLabel: string;
  company: string;
  date: string; // ISO YYYY-MM-DD
  assigneeName?: string;
  team?: string;
  x: number;
  y: number;
}

interface PMSchedulerEvent {
  id: string;
  title: string;
  scheduledDate: string; // ISO "YYYY-MM-DD"
  status: PMStatus;
}

const PM_STATUS_CONFIG: Record<PMStatus, { bg: string; color: string }> = {
  Pending:     { bg: "#f3f4f6", color: "#748094" },
  Open:        { bg: "#f3f4f6", color: "#748094" },
  "In Progress": { bg: "#fef9c3", color: "#b45309" },
  Closed:      { bg: "#fde5f0", color: "#ee0064" },
  Completed:   { bg: "#e0f8e8", color: "#10b981" },
};

// Helper: offset days from today as ISO string
function daysFromToday(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
}

function formatPopupDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function addPopupDays(iso: string, n: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return formatPopupDate(d.toISOString().slice(0, 10));
}

const MOCK_ASSET_PM_EVENTS: Record<string, PMSchedulerEvent[]> = {
  // Monthly Maintenance repeats every 30 days
  // Quarterly Maintenance repeats every 90 days
  // Bi-Annual Maintenance repeats every 180 days
  // Annual Maintenance repeats every 365 days
  sa1: [
    { id: "sa1-pm1", title: "Monthly Maintenance",    scheduledDate: daysFromToday(-30),  status: "Completed" },
    { id: "sa1-pm2", title: "Monthly Maintenance",    scheduledDate: daysFromToday(0),    status: "In Progress" },
    { id: "sa1-pm3", title: "Monthly Maintenance",    scheduledDate: daysFromToday(30),   status: "Pending" },
    { id: "sa1-pm4", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(-90),  status: "Completed" },
    { id: "sa1-pm5", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(2),    status: "Pending" },
    { id: "sa1-pm6", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(90),   status: "Pending" },
    { id: "sa1-pm7", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(-180), status: "Completed" },
    { id: "sa1-pm8", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(4),    status: "Pending" },
    { id: "sa1-pm9", title: "Annual Maintenance",     scheduledDate: daysFromToday(-365), status: "Completed" },
    { id: "sa1-pm10", title: "Annual Maintenance",    scheduledDate: daysFromToday(6),    status: "Pending" },
  ],
  sa2: [
    { id: "sa2-pm1", title: "Monthly Maintenance",    scheduledDate: daysFromToday(-30),  status: "Completed" },
    { id: "sa2-pm2", title: "Monthly Maintenance",    scheduledDate: daysFromToday(1),    status: "Open" },
    { id: "sa2-pm3", title: "Monthly Maintenance",    scheduledDate: daysFromToday(30),   status: "Pending" },
    { id: "sa2-pm4", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(-90),  status: "Completed" },
    { id: "sa2-pm5", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(3),    status: "Pending" },
    { id: "sa2-pm6", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(90),   status: "Pending" },
    { id: "sa2-pm7", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(-180), status: "Completed" },
    { id: "sa2-pm8", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(5),    status: "Pending" },
    { id: "sa2-pm9", title: "Annual Maintenance",     scheduledDate: daysFromToday(-365), status: "Completed" },
    { id: "sa2-pm10", title: "Annual Maintenance",    scheduledDate: daysFromToday(180),  status: "Pending" },
  ],
  sa3: [
    { id: "sa3-pm1", title: "Monthly Maintenance",    scheduledDate: daysFromToday(-30),  status: "Completed" },
    { id: "sa3-pm2", title: "Monthly Maintenance",    scheduledDate: daysFromToday(0),    status: "Open" },
    { id: "sa3-pm3", title: "Monthly Maintenance",    scheduledDate: daysFromToday(30),   status: "Pending" },
    { id: "sa3-pm4", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(-90),  status: "Completed" },
    { id: "sa3-pm5", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(2),    status: "In Progress" },
    { id: "sa3-pm6", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(90),   status: "Pending" },
    { id: "sa3-pm7", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(-180), status: "Completed" },
    { id: "sa3-pm8", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(180),  status: "Pending" },
    { id: "sa3-pm9", title: "Annual Maintenance",     scheduledDate: daysFromToday(-365), status: "Completed" },
    { id: "sa3-pm10", title: "Annual Maintenance",    scheduledDate: daysFromToday(4),    status: "Pending" },
  ],
  sa4: [
    { id: "sa4-pm1", title: "Monthly Maintenance",    scheduledDate: daysFromToday(-30),  status: "Completed" },
    { id: "sa4-pm2", title: "Monthly Maintenance",    scheduledDate: daysFromToday(1),    status: "Pending" },
    { id: "sa4-pm3", title: "Monthly Maintenance",    scheduledDate: daysFromToday(30),   status: "Pending" },
    { id: "sa4-pm4", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(-90),  status: "Closed" },
    { id: "sa4-pm5", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(3),    status: "Pending" },
    { id: "sa4-pm6", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(90),   status: "Pending" },
    { id: "sa4-pm7", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(-180), status: "Completed" },
    { id: "sa4-pm8", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(5),    status: "Pending" },
    { id: "sa4-pm9", title: "Annual Maintenance",     scheduledDate: daysFromToday(-365), status: "Completed" },
    { id: "sa4-pm10", title: "Annual Maintenance",    scheduledDate: daysFromToday(365),  status: "Pending" },
  ],
  sa5: [
    { id: "sa5-pm1", title: "Monthly Maintenance",    scheduledDate: daysFromToday(-30),  status: "Completed" },
    { id: "sa5-pm2", title: "Monthly Maintenance",    scheduledDate: daysFromToday(0),    status: "Open" },
    { id: "sa5-pm3", title: "Monthly Maintenance",    scheduledDate: daysFromToday(30),   status: "Pending" },
    { id: "sa5-pm4", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(-90),  status: "Completed" },
    { id: "sa5-pm5", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(2),    status: "Pending" },
    { id: "sa5-pm6", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(90),   status: "Pending" },
    { id: "sa5-pm7", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(-180), status: "Completed" },
    { id: "sa5-pm8", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(4),    status: "Pending" },
    { id: "sa5-pm9", title: "Annual Maintenance",     scheduledDate: daysFromToday(-365), status: "Completed" },
    { id: "sa5-pm10", title: "Annual Maintenance",    scheduledDate: daysFromToday(6),    status: "Pending" },
  ],
  sa6: [
    { id: "sa6-pm1", title: "Monthly Maintenance",    scheduledDate: daysFromToday(-30),  status: "Completed" },
    { id: "sa6-pm2", title: "Monthly Maintenance",    scheduledDate: daysFromToday(1),    status: "In Progress" },
    { id: "sa6-pm3", title: "Monthly Maintenance",    scheduledDate: daysFromToday(30),   status: "Pending" },
    { id: "sa6-pm4", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(-90),  status: "Completed" },
    { id: "sa6-pm5", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(3),    status: "Pending" },
    { id: "sa6-pm6", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(90),   status: "Pending" },
    { id: "sa6-pm7", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(-180), status: "Completed" },
    { id: "sa6-pm8", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(180),  status: "Pending" },
    { id: "sa6-pm9", title: "Annual Maintenance",     scheduledDate: daysFromToday(-365), status: "Completed" },
    { id: "sa6-pm10", title: "Annual Maintenance",    scheduledDate: daysFromToday(5),    status: "Pending" },
  ],
  sa7: [
    { id: "sa7-pm1", title: "Monthly Maintenance",    scheduledDate: daysFromToday(-30),  status: "Completed" },
    { id: "sa7-pm2", title: "Monthly Maintenance",    scheduledDate: daysFromToday(0),    status: "Pending" },
    { id: "sa7-pm3", title: "Monthly Maintenance",    scheduledDate: daysFromToday(30),   status: "Pending" },
    { id: "sa7-pm4", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(-90),  status: "Closed" },
    { id: "sa7-pm5", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(2),    status: "Pending" },
    { id: "sa7-pm6", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(90),   status: "Pending" },
    { id: "sa7-pm7", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(-180), status: "Completed" },
    { id: "sa7-pm8", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(4),    status: "Pending" },
    { id: "sa7-pm9", title: "Annual Maintenance",     scheduledDate: daysFromToday(-365), status: "Completed" },
    { id: "sa7-pm10", title: "Annual Maintenance",    scheduledDate: daysFromToday(365),  status: "Pending" },
  ],
  sa8: [
    { id: "sa8-pm1", title: "Monthly Maintenance",    scheduledDate: daysFromToday(-30),  status: "Completed" },
    { id: "sa8-pm2", title: "Monthly Maintenance",    scheduledDate: daysFromToday(1),    status: "Pending" },
    { id: "sa8-pm3", title: "Monthly Maintenance",    scheduledDate: daysFromToday(30),   status: "Pending" },
    { id: "sa8-pm4", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(-90),  status: "Completed" },
    { id: "sa8-pm5", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(3),    status: "Pending" },
    { id: "sa8-pm6", title: "Quarterly Maintenance",  scheduledDate: daysFromToday(90),   status: "Pending" },
    { id: "sa8-pm7", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(-180), status: "Completed" },
    { id: "sa8-pm8", title: "Bi-Annual Maintenance",  scheduledDate: daysFromToday(5),    status: "Pending" },
    { id: "sa8-pm9", title: "Annual Maintenance",     scheduledDate: daysFromToday(-365), status: "Completed" },
    { id: "sa8-pm10", title: "Annual Maintenance",    scheduledDate: daysFromToday(6),    status: "Pending" },
  ],
};

function AssetThumbSmall() {
  return (
    <svg width="36" height="36" viewBox="0 0 48 48" fill="none" style={{ flexShrink: 0, display: "block", borderRadius: 8 }}>
      <rect width="48" height="48" rx="8" fill="#E6E8FE"/>
      <path d="M23.47 14.78L20.78 17.47a.75.75 0 000 1.06l2.69 2.69a.75.75 0 001.06 0l2.69-2.69a.75.75 0 000-1.06l-2.69-2.69a.75.75 0 00-1.06 0z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M29.47 20.78l-2.69 2.69a.75.75 0 000 1.06l2.69 2.69a.75.75 0 001.06 0l2.69-2.69a.75.75 0 000-1.06l-2.69-2.69a.75.75 0 00-1.06 0z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M17.47 20.77l-2.69 2.69a.75.75 0 000 1.06l2.69 2.69a.75.75 0 001.06 0l2.69-2.69a.75.75 0 000-1.06l-2.69-2.69a.75.75 0 00-1.06 0z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M23.47 26.77l-2.69 2.69a.75.75 0 000 1.06l2.69 2.69a.75.75 0 001.06 0l2.69-2.69a.75.75 0 000-1.06l-2.69-2.69a.75.75 0 00-1.06 0z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ─── Week View ────────────────────────────────────────────────────────────────

const COL_WIDTH = 110;
const MEMBER_COL_WIDTH = 240;
const ROW_HEIGHT = 60;

interface CalendarColProps {
  colView: "members" | "assets";
  onColViewChange: (v: "members" | "assets") => void;
  members: Member[];
  scheduled: SchedulerWO[];
  dropTarget: { memberId: string; date: string } | null;
  onDrop: (memberId: string, date: string) => void;
  onDragOver: (memberId: string, date: string, e: React.DragEvent) => void;
  onDragLeave: () => void;
  getWOsForCell: (rowId: string, date: Date) => SchedulerWO[];
  getPMEventsForCell: (assetId: string, date: Date) => PMSchedulerEvent[];
  onChipClick: (data: PopupData) => void;
}

interface WeekViewProps extends CalendarColProps {
  days: Date[];
  today: Date;
}

function WeekView({ days, members, today, getWOsForCell, getPMEventsForCell, dropTarget, onDrop, onDragOver, onDragLeave, scheduled, colView, onColViewChange, onChipClick }: WeekViewProps) {
  const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [colDropOpen, setColDropOpen] = React.useState(false);
  const colDropRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!colDropOpen) return;
    function handler(e: MouseEvent) {
      if (colDropRef.current && !colDropRef.current.contains(e.target as Node)) setColDropOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [colDropOpen]);

  const rows: { id: string }[] = colView === "members"
    ? members.map(m => ({ id: m.id }))
    : MOCK_SCHEDULER_ASSETS.map(a => ({ id: a.id }));

  function memberWOCount(memberId: string) {
    return scheduled.filter(wo => wo.assignedMemberId === memberId).length;
  }

  return (
    <div style={{ flex: 1, overflow: "auto" }}>
      <table style={{ borderCollapse: "collapse", minWidth: MEMBER_COL_WIDTH + COL_WIDTH * 7, width: "100%" }}>
        {/* Sticky header row */}
        <thead>
          <tr>
            <th style={{
              width: MEMBER_COL_WIDTH, minWidth: MEMBER_COL_WIDTH,
              position: "sticky", left: 0, top: 0, zIndex: 3,
              background: "#fff", borderBottom: "1px solid #e5e9f0", borderRight: "1px solid var(--color-border-primary)",
              padding: "10px 14px",
              textAlign: "left",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {/* Members / Assets label-dropdown */}
                <div ref={colDropRef} style={{ position: "relative" }}>
                  <button
                    onClick={() => setColDropOpen(o => !o)}
                    style={{
                      display: "flex", alignItems: "center", gap: 4,
                      background: "none", border: "none", cursor: "pointer",
                      padding: 0, fontFamily: "var(--font-family-base)",
                    }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#1a2233" }}>
                      {colView === "members" ? "Members" : "Assets"}
                    </span>
                    <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 4l4 4 4-4" />
                    </svg>
                  </button>
                  {colDropOpen && (
                    <div style={{
                      position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50,
                      background: "#fff", border: "1px solid var(--color-border-primary)",
                      borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                      minWidth: 140, overflow: "hidden",
                    }}>
                      {(["members", "assets"] as const).map(v => (
                        <button key={v} onClick={() => { onColViewChange(v); setColDropOpen(false); }} style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          width: "100%", padding: "9px 14px",
                          fontSize: 12, fontWeight: colView === v ? 700 : 500,
                          color: colView === v ? "#1a2233" : "#748094",
                          background: colView === v ? "#f0f2f7" : "transparent",
                          border: "none", cursor: "pointer", fontFamily: "var(--font-family-base)",
                          textTransform: "capitalize",
                        }}>
                          {v === "members" ? "Members" : "Assets"}
                          {colView === v && (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#6974fb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M2 6l3 3 5-5" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Action icons */}
                <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <button style={iconBtnStyle} aria-label="Filter">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#9ca3af" strokeWidth="1.6" strokeLinecap="round">
                      <path d="M2 4h2m8 0h2M4 4a2 2 0 004 0M4 4a2 2 0 014 0M2 8h6m4 0h2M8 8a2 2 0 004 0M8 8a2 2 0 014 0M2 12h2m8 0h2M4 12a2 2 0 004 0M4 12a2 2 0 014 0" />
                    </svg>
                  </button>
                  <button style={iconBtnStyle} aria-label="Sort">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#9ca3af" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 4h12M4 8h8M6 12h4" />
                    </svg>
                  </button>
                  <button style={iconBtnStyle} aria-label={colView === "members" ? "Add member" : "Add asset"}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#9ca3af" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.5 14v-1a3 3 0 00-3-3h-4a3 3 0 00-3 3v1" />
                      <circle cx="5.5" cy="6" r="2.5" />
                    </svg>
                  </button>
                </div>
              </div>
            </th>
            {days.map((day, i) => {
              const isToday = sameDate(day, today);
              return (
                <th key={i} style={{
                  width: COL_WIDTH, minWidth: COL_WIDTH,
                  position: "sticky", top: 0, zIndex: 2,
                  background: isToday ? "#f0f2fe" : "#fff",
                  borderBottom: "1px solid #e5e9f0",
                  borderRight: "1px solid #f0f2f7",
                  padding: "8px 6px",
                  textAlign: "center",
                  fontSize: 11,
                }}>
                  <div style={{ fontWeight: 500, color: "#748094", textTransform: "uppercase", fontSize: 10, letterSpacing: 0.5 }}>{DAY_NAMES[i]}</div>
                  <div style={{
                    fontWeight: 700, fontSize: 18,
                    color: isToday ? "#6974fb" : "#1a2233",
                    width: 32, height: 32,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    borderRadius: "50%",
                    background: isToday ? "#e6e8fe" : "transparent",
                    margin: "2px auto 0",
                  }}>
                    {day.getDate()}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        {/* Rows */}
        <tbody>
          {rows.map(row => {
            const member = colView === "members" ? members.find(m => m.id === row.id) : undefined;
            const asset  = colView === "assets"  ? MOCK_SCHEDULER_ASSETS.find(a => a.id === row.id) : undefined;
            return (
            <tr key={row.id}>
              {/* Left column cell */}
              <td style={{
                position: "sticky", left: 0, zIndex: 1,
                width: MEMBER_COL_WIDTH, minWidth: MEMBER_COL_WIDTH, maxWidth: MEMBER_COL_WIDTH,
                background: "#fff",
                borderBottom: "1px solid #f0f2f7",
                borderRight: "1px solid var(--color-border-primary)",
                padding: "6px 12px",
                verticalAlign: "middle",
                overflow: "hidden",
              }}>
                {member && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: "50%",
                        background: "#e6e8fe", flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 9, fontWeight: 700, color: "#6974fb",
                      }}>
                        {member.initials}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#1a2233", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{member.name}</span>
                    </div>
                    {memberWOCount(member.id) > 0 && (
                      <span style={{
                        minWidth: 20, height: 20, borderRadius: 10,
                        background: "#f0f2f7", flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, fontWeight: 700, color: "#748094",
                        padding: "0 5px",
                      }}>
                        {memberWOCount(member.id)}
                      </span>
                    )}
                  </div>
                )}
                {asset && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <AssetThumbSmall />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#1a2233", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {asset.name}
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 500, marginTop: 1 }}>
                        {asset.serial}
                      </div>
                    </div>
                  </div>
                )}
              </td>

              {/* Day cells */}
              {days.map((day, di) => {
                const iso = isoDate(day);
                const wos = colView === "members" ? getWOsForCell(row.id, day) : [];
                const pmEvents = colView === "assets" ? getPMEventsForCell(row.id, day) : [];
                const isDropTarget = dropTarget?.memberId === row.id && dropTarget?.date === iso;
                const isToday = sameDate(day, today);
                const isWeekend = di >= 5;
                return (
                  <td
                    key={di}
                    onDrop={() => onDrop(row.id, iso)}
                    onDragOver={e => onDragOver(row.id, iso, e)}
                    onDragLeave={onDragLeave}
                    style={{
                      borderBottom: "1px solid #f0f2f7",
                      borderRight: "1px solid #f0f2f7",
                      verticalAlign: "top",
                      minHeight: ROW_HEIGHT,
                      height: ROW_HEIGHT,
                      padding: "6px 4px",
                      background: isDropTarget
                        ? "#eef0ff"
                        : isToday
                        ? "#fafafe"
                        : isWeekend
                        ? "#fafafa"
                        : "#fff",
                      outline: isDropTarget ? "2px dashed #6974fb" : "none",
                      outlineOffset: -2,
                      transition: "background 0.1s",
                    }}
                  >
                    {wos.map(wo => (
                      <WOChip key={wo.id} wo={wo} onClick={ev => {
                        ev.stopPropagation();
                        const mem = MOCK_MEMBERS.find(m => m.id === wo.assignedMemberId);
                        onChipClick({ type: "wo", title: wo.title, assetLabel: `${wo.assetName} • ${wo.number}`, company: wo.company, date: wo.scheduledDate!, assigneeName: mem?.name, team: wo.team, x: ev.clientX, y: ev.clientY });
                      }} />
                    ))}
                    {pmEvents.map(e => {
                      const ast = MOCK_SCHEDULER_ASSETS.find(a => a.id === row.id);
                      return (
                        <PMEventChip key={e.id} event={e} onClick={ev => {
                          ev.stopPropagation();
                          onChipClick({ type: "pm", title: e.title, assetLabel: ast ? `${ast.name} • ${ast.serial}` : e.title, company: ast?.company ?? "", date: e.scheduledDate, x: ev.clientX, y: ev.clientY });
                        }} />
                      );
                    })}
                  </td>
                );
              })}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Month View ───────────────────────────────────────────────────────────────

interface MonthViewProps extends CalendarColProps {
  anchor: Date;
  today: Date;
}

const MONTH_COL_WIDTH = 90;
const DAY_ABBR = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function MonthView({ anchor, today, members, scheduled, getWOsForCell, getPMEventsForCell, dropTarget, onDrop, onDragOver, onDragLeave, colView, onColViewChange, onChipClick }: MonthViewProps) {
  const [colDropOpen, setColDropOpen] = React.useState(false);
  const colDropRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!colDropOpen) return;
    function handler(e: MouseEvent) {
      if (colDropRef.current && !colDropRef.current.contains(e.target as Node)) setColDropOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [colDropOpen]);

  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthDays = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));

  const rows: { id: string }[] = colView === "members"
    ? members.map(m => ({ id: m.id }))
    : MOCK_SCHEDULER_ASSETS.map(a => ({ id: a.id }));

  function memberWOCount(rowId: string) {
    return scheduled.filter(wo => wo.assignedMemberId === rowId).length;
  }

  return (
    <div style={{ flex: 1, overflow: "auto" }}>
      <table style={{ borderCollapse: "collapse", minWidth: MEMBER_COL_WIDTH + MONTH_COL_WIDTH * daysInMonth, width: "100%" }}>
        <thead>
          <tr>
            {/* Sticky members/assets header cell */}
            <th style={{
              width: MEMBER_COL_WIDTH, minWidth: MEMBER_COL_WIDTH,
              position: "sticky", left: 0, top: 0, zIndex: 3,
              background: "#fff", borderBottom: "1px solid #e5e9f0", borderRight: "1px solid var(--color-border-primary)",
              padding: "10px 14px", textAlign: "left",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div ref={colDropRef} style={{ position: "relative" }}>
                  <button onClick={() => setColDropOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font-family-base)" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#1a2233" }}>{colView === "members" ? "Members" : "Assets"}</span>
                    <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4l4 4 4-4" /></svg>
                  </button>
                  {colDropOpen && (
                    <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, background: "#fff", border: "1px solid var(--color-border-primary)", borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: 140, overflow: "hidden" }}>
                      {(["members", "assets"] as const).map(v => (
                        <button key={v} onClick={() => { onColViewChange(v); setColDropOpen(false); }} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "9px 14px", fontSize: 12, fontWeight: colView === v ? 700 : 500, color: colView === v ? "#1a2233" : "#748094", background: colView === v ? "#f0f2f7" : "transparent", border: "none", cursor: "pointer", fontFamily: "var(--font-family-base)", textTransform: "capitalize" }}>
                          {v === "members" ? "Members" : "Assets"}
                          {colView === v && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#6974fb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6l3 3 5-5" /></svg>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <button style={iconBtnStyle} aria-label="Filter">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#9ca3af" strokeWidth="1.6" strokeLinecap="round"><path d="M2 4h2m8 0h2M4 4a2 2 0 004 0M4 4a2 2 0 014 0M2 8h6m4 0h2M8 8a2 2 0 004 0M8 8a2 2 0 014 0M2 12h2m8 0h2M4 12a2 2 0 004 0M4 12a2 2 0 014 0" /></svg>
                  </button>
                  <button style={iconBtnStyle} aria-label="Sort">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#9ca3af" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4h12M4 8h8M6 12h4" /></svg>
                  </button>
                  <button style={iconBtnStyle} aria-label="Add">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#9ca3af" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10.5 14v-1a3 3 0 00-3-3h-4a3 3 0 00-3 3v1" /><circle cx="5.5" cy="6" r="2.5" /></svg>
                  </button>
                </div>
              </div>
            </th>

            {/* Day columns */}
            {monthDays.map((day, i) => {
              const isToday = sameDate(day, today);
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              return (
                <th key={i} style={{
                  width: MONTH_COL_WIDTH, minWidth: MONTH_COL_WIDTH,
                  position: "sticky", top: 0, zIndex: 2,
                  background: isToday ? "#f0f2fe" : isWeekend ? "#fafafa" : "#fff",
                  borderBottom: "1px solid #e5e9f0",
                  borderRight: "1px solid #f0f2f7",
                  padding: "6px 4px",
                  textAlign: "center",
                }}>
                  <div style={{ fontWeight: 500, color: "#748094", textTransform: "uppercase", fontSize: 10, letterSpacing: 0.4 }}>{DAY_ABBR[day.getDay()]}</div>
                  <div style={{
                    fontWeight: 700, fontSize: 16,
                    color: isToday ? "#6974fb" : "#1a2233",
                    width: 28, height: 28,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    borderRadius: "50%",
                    background: isToday ? "#e6e8fe" : "transparent",
                    margin: "2px auto 0",
                  }}>
                    {day.getDate()}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {rows.map(row => {
            const member = colView === "members" ? members.find(m => m.id === row.id) : undefined;
            const asset  = colView === "assets"  ? MOCK_SCHEDULER_ASSETS.find(a => a.id === row.id) : undefined;
            return (
              <tr key={row.id}>
                {/* Sticky row label cell */}
                <td style={{
                  position: "sticky", left: 0, zIndex: 1,
                  background: "#fff",
                  borderBottom: "1px solid #f0f2f7",
                  borderRight: "1px solid #e5e9f0",
                  padding: "6px 12px",
                  verticalAlign: "middle",
                }}>
                  {member && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#e6e8fe", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#6974fb" }}>
                          {member.initials}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#1a2233", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{member.name}</span>
                      </div>
                      {memberWOCount(member.id) > 0 && (
                        <span style={{ minWidth: 20, height: 20, borderRadius: 10, background: "#f0f2f7", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#748094", padding: "0 5px" }}>
                          {memberWOCount(member.id)}
                        </span>
                      )}
                    </div>
                  )}
                  {asset && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <AssetThumbSmall />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#1a2233", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{asset.name}</div>
                        <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 500, marginTop: 1 }}>{asset.serial}</div>
                      </div>
                    </div>
                  )}
                </td>

                {/* Day cells */}
                {monthDays.map((day, di) => {
                  const iso = isoDate(day);
                  const wos = colView === "members" ? getWOsForCell(row.id, day) : [];
                  const pmEvents = colView === "assets" ? getPMEventsForCell(row.id, day) : [];
                  const isDropTarget = dropTarget?.memberId === row.id && dropTarget?.date === iso;
                  const isToday = sameDate(day, today);
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  return (
                    <td
                      key={di}
                      onDrop={() => onDrop(row.id, iso)}
                      onDragOver={e => onDragOver(row.id, iso, e)}
                      onDragLeave={onDragLeave}
                      style={{
                        borderBottom: "1px solid #f0f2f7",
                        borderRight: "1px solid #f0f2f7",
                        verticalAlign: "top",
                        minHeight: ROW_HEIGHT,
                        height: ROW_HEIGHT,
                        padding: "5px 4px",
                        background: isDropTarget ? "#eef0ff" : isToday ? "#fafafe" : isWeekend ? "#fafafa" : "#fff",
                        outline: isDropTarget ? "2px dashed #6974fb" : "none",
                        outlineOffset: -2,
                        transition: "background 0.1s",
                      }}
                    >
                      {wos.map(wo => (
                        <WOChip key={wo.id} wo={wo} onClick={ev => {
                          ev.stopPropagation();
                          const mem = MOCK_MEMBERS.find(m => m.id === wo.assignedMemberId);
                          onChipClick({ type: "wo", title: wo.title, assetLabel: `${wo.assetName} • ${wo.number}`, company: wo.company, date: wo.scheduledDate!, assigneeName: mem?.name, team: wo.team, x: ev.clientX, y: ev.clientY });
                        }} />
                      ))}
                      {pmEvents.map(e => {
                        const ast = MOCK_SCHEDULER_ASSETS.find(a => a.id === row.id);
                        return (
                          <PMEventChip key={e.id} event={e} onClick={ev => {
                            ev.stopPropagation();
                            onChipClick({ type: "pm", title: e.title, assetLabel: ast ? `${ast.name} • ${ast.serial}` : e.title, company: ast?.company ?? "", date: e.scheduledDate, x: ev.clientX, y: ev.clientY });
                          }} />
                        );
                      })}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const navBtnStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: "5px 8px",
  border: "1px solid #e5e9f0", borderRadius: 7, background: "#fff",
  cursor: "pointer", color: "#748094",
  fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 600,
};

const iconBtnStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "center",
  width: 26, height: 26,
  background: "none", border: "none",
  cursor: "pointer", borderRadius: 6, padding: 0,
};
